import { createAsyncThunk } from '@reduxjs/toolkit'
import toCamel from 'lodash-humps'
import {
  createFetchAsyncThunk,
  csrfToken,
  toSnake,
  throwErrorForErrorResponse,
  throwTextErrorForErrorResponse,
  errorHandler,
  ErrorPayload
} from '../common'
import { closeModal } from './settingSlice'
import type { SettingState } from './types'

type ThunkConfig = {
  state: { common: { baseUri: string }; setting: SettingState }
  rejectValue: ErrorPayload
}

type CreateSettingParams = {
  navigate: (path: string) => void
  formData: FormData
}

type ModifySettingParams = {
  settingId: number | string
  formData: FormData
}

type SettingIdParams = {
  id: number | string
} & Record<string, unknown>

type CopySettingParams = {
  id: number | string
  navigate: (path: string) => void
} & Record<string, unknown>

type SettingDetailParams = {
  settingId: number | string
  settingName: string
} & Record<string, unknown>

type SettingDetailIdParams = {
  settingId: number | string
  settingName: string
  id: number | string
} & Record<string, unknown>

type ArticleRulesGroupParams = {
  settingId: number | string
} & Record<string, unknown>

type ArticleRulesGroupIdParams = {
  settingId: number | string
  id: number | string
} & Record<string, unknown>

type ArticleRulesPageSettingParams = {
  settingId: number | string
  groupId: number | string
} & Record<string, unknown>

export const fetchSettings = createFetchAsyncThunk('setting/fetchSettings', 'settings')

export const createSetting = createAsyncThunk<unknown, CreateSettingParams, ThunkConfig>(
  'setting/createSetting',
  async ({ navigate, formData }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}settings`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { settingId } = payload as { settingId: string }
        navigate(`${baseUri}settings/${settingId}`)
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)

export const showSetting = createAsyncThunk<unknown, { settingId: number | string }, ThunkConfig>(
  'setting/showSetting',
  async ({ settingId }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}settings/${settingId}`, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(throwTextErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const modifySetting = createAsyncThunk<unknown, ModifySettingParams, ThunkConfig>(
  'setting/modifySetting',
  async ({ settingId, formData }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}settings/${settingId}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().setting
        dispatch(fetchSettings({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const deleteSetting = createAsyncThunk<unknown, SettingIdParams, ThunkConfig>(
  'setting/deleteSetting',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}settings/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().setting
        dispatch(fetchSettings({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const copySetting = createAsyncThunk<unknown, CopySettingParams, ThunkConfig>(
  'setting/copySetting',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { navigate, ...bodyParams } = params

    return fetch(`${baseUri}settings/${bodyParams.id}/copy`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(bodyParams))
    })
      .then(throwErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { settingId } = payload as { settingId: string }
        navigate(`${baseUri}settings/${settingId}`)
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const exportSetting = createAsyncThunk<unknown, { id: number | string; fileName: string }, ThunkConfig>(
  'setting/exportSetting',
  async ({ id }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}settings/${id}/export.zip`)
      .then(throwErrorForErrorResponse)
      .then((response) => response.blob())
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const modifySettingDetail = createAsyncThunk<unknown, SettingDetailIdParams, ThunkConfig>(
  'setting/modifySettingDetail',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id, settingId, settingName } = params

    return fetch(`${baseUri}settings/${settingId}/${settingName}/${id}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const createSettingDetail = createAsyncThunk<unknown, SettingDetailParams, ThunkConfig>(
  'setting/createSettingDetail',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId, settingName } = params

    return fetch(`${baseUri}settings/${settingId}/${settingName}`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(closeModal())
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)

export const deleteSettingDetail = createAsyncThunk<unknown, SettingDetailIdParams, ThunkConfig>(
  'setting/deleteSettingDetail',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId, settingName, id } = params

    return fetch(`${baseUri}settings/${settingId}/${settingName}/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const createArticleRulesGroup = createAsyncThunk<unknown, ArticleRulesGroupParams, ThunkConfig>(
  'setting/createArticleRulesGroup',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId } = params

    return fetch(`${baseUri}settings/${settingId}/article_rules_groups`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const updateArticleRulesGroup = createAsyncThunk<unknown, ArticleRulesGroupIdParams, ThunkConfig>(
  'setting/updateArticleRulesGroup',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId, id } = params

    return fetch(`${baseUri}settings/${settingId}/article_rules_groups/${id}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const updateArticleRulesPageSetting = createAsyncThunk<unknown, ArticleRulesPageSettingParams, ThunkConfig>(
  'setting/updateArticleRulesPageSetting',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId, groupId } = params

    return fetch(`${baseUri}settings/${settingId}/article_rules_groups/${groupId}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const deleteArticleRulesGroup = createAsyncThunk<unknown, ArticleRulesGroupIdParams, ThunkConfig>(
  'setting/deleteArticleRulesGroup',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { settingId, id } = params

    return fetch(`${baseUri}settings/${settingId}/article_rules_groups/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        dispatch(showSetting({ settingId }))
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)
